import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './Home.css';

const CATEGORY_ITEMS = [
  'เตียงนอน',
  'โซฟา',
  'ชั้นวางทีวี',
  'โต๊ะเครื่องแป้ง',
  'ตู้เสื้อผ้า',
  'ฟูกนอน/ที่นอน',
  'ตู้โชว์',
  'หิ้งพระ',
  'ตู้กับข้าว',
  'ชุดเซ็ต'
];

// รายการ product_id ที่เป็นสินค้ายอดนิยม (แก้ไขได้ตามต้องการ)
const POPULAR_PRODUCT_IDS = [
  // ใส่ product_id ที่ต้องการให้แสดงเป็นยอดนิยมที่นี่
  // ตัวอย่าง: 1, 2, 3
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('popular');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const toNumber = (value, fallback = Infinity) => {
    if (value == null || value === '') return fallback;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/400';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `/images/${imagePath}`;
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/api/products');
        if (response.data && response.data.length > 0) {
          const normalized = response.data.map((item) => {
            const images = Array.isArray(item.product_images)
              ? item.product_images
                .map((img) => (img == null ? '' : String(img).trim()))
                .filter(Boolean)
              : item.product_image
                ? [String(item.product_image).trim()]
                : [];
            const heroImage = images[0] || (item.product_image ? String(item.product_image).trim() : '');
            return {
              ...item,
              product_image: heroImage,
              product_images: images,
              tags: Array.isArray(item.tags) ? item.tags : [],
              highlights: Array.isArray(item.highlights) ? item.highlights : []
            };
          });

          setProducts(normalized);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);


  const filteredProducts = useMemo(() => {
    let result = products;

    if (selectedCategory) {
      result = result.filter((product) =>
        product.tags?.some((tag) => tag === selectedCategory)
      );
    }

    if (searchTerm.trim()) {
      const keyword = searchTerm.toLowerCase();
      result = result.filter((product) => {
        const name = product.product_name?.toLowerCase() || '';
        const description = product.product_description?.toLowerCase() || '';
        return name.includes(keyword) || description.includes(keyword);
      });
    }

    if (activeFilter === 'promotion') {
      const promotionProducts = result.filter((product) => {
        const baseCash = toNumber(product.price_cash);
        const promoCash = toNumber(product.price_cash_promo);
        const baseInstallment = toNumber(product.price_installment);
        const promoInstallment = toNumber(product.price_installment_promo);

        const hasCashPromo =
          promoCash !== Infinity && baseCash !== Infinity && promoCash < baseCash;
        const hasInstallmentPromo =
          promoInstallment !== Infinity &&
          baseInstallment !== Infinity &&
          promoInstallment < baseInstallment;

        return hasCashPromo || hasInstallmentPromo;
      });

      return promotionProducts.sort((a, b) => {
        const priceA = toNumber(a.price_cash_promo ?? a.price_cash);
        const priceB = toNumber(b.price_cash_promo ?? b.price_cash);
        return priceA - priceB;
      });
    }

    if (activeFilter === 'popular') {
      // ถ้ามีรายการยอดนิยม ให้แสดงเฉพาะสินค้าเหล่านั้น
      // ถ้าไม่มีรายการ ให้แสดงสินค้าทั้งหมด
      if (POPULAR_PRODUCT_IDS.length > 0) {
        const popularProducts = result.filter((product) =>
          POPULAR_PRODUCT_IDS.includes(product.product_id)
        );
        return popularProducts;
      }
      // ถ้าไม่มีรายการยอดนิยม ให้แสดงสินค้าทั้งหมด
      return result;
    }

    return result;
  }, [products, searchTerm, activeFilter, selectedCategory]);

  const handleFilterToggle = (filterKey) => {
    setActiveFilter((prev) => (prev === filterKey ? 'popular' : filterKey));
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategory((prev) => (prev === category ? null : category));
  };


  return (
    <div className="home-page">
      <section className="home-content" id="catalog">
        <div className="home-layout">
          <aside className="category-panel">
            <h2>หมวดหมู่</h2>
            <ul>
              {CATEGORY_ITEMS.map((item) => (
                <li key={item}>
                  <button
                    type="button"
                    className={selectedCategory === item ? 'active' : ''}
                    onClick={() => handleCategoryToggle(item)}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="product-section">
            <div className="product-toolbar">
              <div className="product-search">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="ค้นหาสินค้า..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <div className="product-filters">
                <button
                  type="button"
                  className={`toolbar-btn${activeFilter === 'promotion' ? ' active' : ''}`}
                  onClick={() => handleFilterToggle('promotion')}
                >
                  โปรโมชั่น
                </button>
                <button
                  type="button"
                  className={`toolbar-btn${activeFilter === 'popular' ? ' active' : ''}`}
                  onClick={() => handleFilterToggle('popular')}
                >
                  ยอดนิยม
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="home-loading">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="product-grid">
                {filteredProducts.map((product) => {
                  const finalCashPrice = product.price_cash_promo ?? product.price_cash;
                  const baseCashPrice = product.price_cash;
                  const installmentPrice =
                    product.price_installment_promo ?? product.price_installment ?? null;
                  const hasCashPromo =
                    baseCashPrice != null &&
                    product.price_cash_promo != null &&
                    parseFloat(product.price_cash_promo) < parseFloat(baseCashPrice);
                  const hasInstallmentPromo =
                    product.price_installment != null &&
                    product.price_installment_promo != null &&
                    parseFloat(product.price_installment_promo) <
                    parseFloat(product.price_installment);
                  const isPromo = hasCashPromo || hasInstallmentPromo;

                  return (
                    <Link
                      to={`/products/${product.product_id}`}
                      key={product.product_id}
                      className={`product-card${isPromo ? ' product-card--promo' : ''}`}
                    >
                      <div className="product-image-wrapper">
                        <img src={getImageUrl(product.product_image)} alt={product.product_name} />
                        {isPromo && <span className="promo-badge">โปรโมชัน</span>}
                      </div>
                      <div className="product-card-body">
                        <h3>{product.product_name}</h3>
                        <div className="product-price">
                          {finalCashPrice && (
                            <span className="price-current">
                              ฿{parseFloat(finalCashPrice).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {installmentPrice && (
                          <p className="price-installment">
                            ผ่อนเริ่มต้น ฿{parseFloat(installmentPrice).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

